import type { Audit, Comment } from '@/models/registry';
import type { AuditLog } from '@/models/types';

import {
  FEATURE_FLAG_NAMES,
  useIsFeatureEnabled,
} from '@/hooks/use-feature-flags';
import { observer } from 'mobx-react-lite';
import AuditLine from './audit-line';
import CombinedAuditLine, {
  type CombinedAuditLog,
} from './combined-audit-line';
import Thread from './thread';
import ThreadDeIdEd from './thread-de-id-ed';

export type AuditTrailLogs = (AuditLog | CombinedAuditLog)[];

// PRODUCT NOTE: the audit trail is a list of things that happened to the deal.
// .i.e. a list of audit logs, comments, and combined audit logs.
// (Thus, it does not include the add comment button because
// that has not happened yet.)
const AuditTrail = observer(
  ({
    auditLogs,
    audits,
    comments,
    dealId,
  }: {
    auditLogs: AuditTrailLogs;
    audits: Audit[];
    comments: Comment[];
    dealId: string;
  }) => {
    const { isFeatureEnabled: isDeidThreadEnabled } = useIsFeatureEnabled({
      featureFlagName: FEATURE_FLAG_NAMES.DEID_THREAD,
    });

    return (
      <>
        {auditLogs.map((auditLog) => {
          switch (auditLog.type) {
            case 'audit':
              const audit = audits.find((a) => a.id === auditLog.id);
              if (!audit) return null;

              return <AuditLine key={audit.id} audit={audit} />;
            case 'comment':
              const comment = comments.find((c) => c.id === auditLog.id);
              if (!comment) return null;

              if (isDeidThreadEnabled) {
                return (
                  <div
                    key={comment.id}
                    className="flex flex-row gap-2 flex-nowrap"
                  >
                    <div className="w-1/2">
                      <Thread
                        key={comment.id}
                        comment={comment}
                        dealId={dealId}
                      />
                    </div>
                    <div className="w-1/2">
                      <ThreadDeIdEd
                        key={comment.id}
                        comment={comment}
                        dealId={dealId}
                      />
                    </div>
                  </div>
                );
              }

              return (
                <Thread key={comment.id} comment={comment} dealId={dealId} />
              );
            case 'combined-audit':
              return (
                <CombinedAuditLine
                  key={`combined-audit-${auditLog.groupId}`}
                  auditLog={auditLog}
                  audits={audits}
                />
              );
          }
        })}
      </>
    );
  },
);

export default AuditTrail;
